import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, MapPin, Hash, AlertTriangle, Lock } from 'lucide-react';
import { getIcon, getEdgeColor, STATUS_CFG, getImageUrl } from '@/components/giraffe';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function dateHeader(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export default function TripView() {
  const { slug } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordMeta, setPasswordMeta] = useState({ title: '', cover_image: '' });
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState(false);
  const [postcards, setPostcards] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/giraffe/trip/${slug}/postcards`)
      .then((r) => setPostcards(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPostcards([]));
  }, [slug]);

  const load = useCallback((pw) => {
    setError(null);
    setPwError(false);
    axios
      .get(`${API}/public/trip/${slug}`, { params: pw ? { pw } : {} })
      .then((r) => {
        setTrip(r.data);
        setNeedsPassword(false);
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 403 && detail?.error === 'password_required') {
          setPasswordMeta({ title: detail.title || '', cover_image: detail.cover_image || '' });
          setNeedsPassword(true);
          if (pw) setPwError(true);
        } else {
          setError('notfound');
        }
      });
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
      <div className="text-center">
        <h1 className="text-2xl text-[#f0f0f5] font-serif mb-2">Not Found</h1>
        <p className="text-[#b0b4c0] font-light">This trip isn't available.</p>
      </div>
    </div>
  );

  if (needsPassword) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
      <div className="w-full max-w-sm text-center">
        <div className="w-12 h-12 rounded-full bg-white/[0.05] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
          <Lock size={20} className="text-[#9B6DD4]" />
        </div>
        <h1 className="text-2xl text-[#f0f0f5] font-serif mb-1">{passwordMeta.title || 'Private Trip'}</h1>
        <p className="text-[#b0b4c0] font-light mb-6">Enter the password to view this itinerary.</p>
        <form onSubmit={(e) => { e.preventDefault(); load(pwInput); }}>
          <input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Password"
            autoFocus
            className={`w-full bg-white/[0.05] border ${pwError ? 'border-[#E24B4A]/50' : 'border-white/[0.06]'} rounded-lg px-3 py-2 text-sm text-[#f0f0f5] placeholder:text-[#b0b4c0]/40 focus:border-[#6B35B8] focus:outline-none`}
          />
          {pwError && <p className="text-xs text-[#E24B4A] mt-2 font-mono">Incorrect password.</p>}
          <button type="submit" className="w-full mt-3 bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-lg py-2 text-sm font-medium transition-colors">
            View Itinerary
          </button>
        </form>
      </div>
    </div>
  );

  if (!trip) return (
    <div className="min-h-screen bg-[#0a0a0f]" style={{ padding: '48px 24px' }}>
      <ZLoadingBar accent="#10B981" loading={true} />
    </div>
  );

  const logistics = (trip.entries || [])
    .filter((e) => e.entry_type === 'logistics')
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  // Group by date, preserving sorted order
  const groups = [];
  let lastDate = null;
  for (const e of logistics) {
    if (e.date !== lastDate) {
      groups.push({ date: e.date, entries: [e] });
      lastDate = e.date;
    } else {
      groups[groups.length - 1].entries.push(e);
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="trip-view-page">
      {/* Hero */}
      <div className="relative min-h-[42vh] flex items-end">
        {trip.cover_image ? (
          <img src={getImageUrl(trip.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[#10B981]/15 to-[#0a0a0f]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/50 to-transparent" />
        <div className="relative z-10 p-8 md:p-16 max-w-4xl">
          <p className="text-emerald-400/80 font-mono text-[11px] tracking-[0.2em] uppercase mb-3">Itinerary</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-serif font-medium tracking-tight leading-none mb-4" data-testid="trip-view-title">
            {trip.title}
          </h1>
          {trip.description && <p className="text-white/70 font-light text-base md:text-lg max-w-xl">{trip.description}</p>}
          {trip.start_date && (
            <p className="text-white/40 font-mono text-xs tracking-wider mt-4">
              {trip.start_date}{trip.end_date ? ` — ${trip.end_date}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-10">
        {groups.length === 0 && (
          <p className="text-center text-[#b0b4c0]/60 font-light py-16">No itinerary entries to show yet.</p>
        )}
        {groups.map((group, gi) => (
          <div key={group.date || gi} className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-[11px] font-mono tracking-[0.15em] text-[#b0b4c0]/70 uppercase">{dateHeader(group.date)}</span>
              <div className="flex-1 h-px bg-white/[0.05]" />
            </div>
            <div className="space-y-3">
              {group.entries.map((entry) => {
                const Icon = getIcon(entry.flag);
                const edge = getEdgeColor(entry);
                const status = STATUS_CFG[entry.status];
                const isHotel = entry.logistics_type === 'hotel';
                return (
                  <div
                    key={entry.id}
                    data-testid={`view-entry-${entry.id}`}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex gap-3"
                    style={{ borderLeft: `2px solid ${edge}` }}
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">
                      <Icon size={18} style={{ color: edge }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <span className="flex-1 min-w-0 text-sm text-[#f0f0f5] font-medium">{entry.title}</span>
                        {status && (
                          <span className={`text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-full shrink-0 ${status.bg} ${status.text}`}>
                            {status.label}
                          </span>
                        )}
                      </div>

                      {/* Meta line */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] font-mono text-[#b0b4c0]/70">
                        {isHotel ? (
                          <span>
                            <span className="text-[#b0b4c0]/50">IN</span> {entry.date}
                            {entry.end_date && <> <span className="text-[#b0b4c0]/50">· OUT</span> {entry.end_date}</>}
                          </span>
                        ) : (
                          entry.end_date && entry.end_date !== entry.date && (
                            <span>{entry.date} → {entry.end_date}</span>
                          )
                        )}
                        {entry.time && (
                          <span className="flex items-center gap-1"><Clock size={11} /> {entry.time}</span>
                        )}
                        {entry.location && (
                          <span className="flex items-center gap-1"><MapPin size={11} /> {entry.location}</span>
                        )}
                        {entry.confirmation && (
                          <span className="flex items-center gap-1"><Hash size={11} /> {entry.confirmation}</span>
                        )}
                      </div>

                      {entry.note && <p className="text-xs text-[#b0b4c0] mt-2 whitespace-pre-wrap">{entry.note}</p>}
                      {entry.content && <p className="text-xs text-[#f0f0f5]/70 mt-2 whitespace-pre-wrap">{entry.content}</p>}

                      {entry.alert && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-400/90">
                          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                          <span className="whitespace-pre-wrap">{entry.alert}</span>
                        </div>
                      )}

                      {Array.isArray(entry.items) && entry.items.filter((it) => it.text).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {entry.items.filter((it) => it.text).map((it, j) => (
                            <li key={j} className="text-xs text-[#b0b4c0] flex items-start gap-2">
                              <span className="text-[#b0b4c0]/40 mt-0.5">•</span>
                              <span>{it.text}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {Array.isArray(entry.images) && entry.images.length > 0 && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {entry.images.map((img, j) => (
                            <img
                              key={j}
                              src={getImageUrl(img)}
                              alt=""
                              className="w-20 h-20 rounded-lg object-cover border border-white/[0.06]"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Postcards linked to this trip */}
      {postcards.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 pb-12" data-testid="trip-view-postcards">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[11px] font-mono tracking-[0.15em] text-emerald-400/80 uppercase">Postcards</span>
            <div className="flex-1 h-px bg-white/[0.05]" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {postcards.map((pc) => (
              <a
                key={pc.id}
                href={`/gallery/postcard/${pc.public_slug || pc.id}`}
                className="block aspect-[4/5] overflow-hidden rounded-md border border-white/[0.06] bg-black/20 hover:border-white/[0.15] transition-colors"
              >
                {pc.composed_image_url && (
                  <img src={pc.composed_image_url} alt={pc.title || ''} loading="lazy" className="w-full h-full object-contain" />
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center py-12">
        <p className="text-[#b0b4c0]/20 text-xs font-mono tracking-[0.3em]">WERNER LABS</p>
      </div>
    </div>
  );
}
